import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import StudyItem from '../StudyItem';
import LegacyButtonGroup from '../LegacyButtonGroup';
import LegacyButton from '../LegacyButton';
import ThumbnailList from '../ThumbnailList';
import { StringNumber } from '../../types';
import { decodeJWT } from '@/Decryption.js';
import { useLocation } from 'react-router-dom';

const getTrackedSeries = displaySets => {
  let trackedSeries = 0;
  displaySets.forEach(displaySet => {
    if (displaySet.isTracked) {
      trackedSeries++;
    }
  });
  return trackedSeries;
};

const StudyBrowser = ({
  tabs,
  activeTabName,
  expandedStudyInstanceUIDs,
  onClickTab,
  onClickStudy,
  onClickThumbnail,
  onDoubleClickThumbnail,
  onClickUntrack,
  activeDisplaySetInstanceUIDs,
  servicesManager,
}: withAppTypes) => {
  const { t } = useTranslation('StudyBrowser');
  const { customizationService, userAuthenticationService } = servicesManager?.services || {};
  const authorizationHeader = userAuthenticationService.getAuthorizationHeader()
    ? userAuthenticationService.getAuthorizationHeader().Authorization
    : '';
  const location = useLocation();

  // --- Parse token ---
  const tokenObject = authorizationHeader
    ? decodeJWT(authorizationHeader.replace('Bearer ', ''))?.payload ?? null
    : null;

  const tokenStudies: string[] = tokenObject?.Studies
    ? String(tokenObject.Studies)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    : [];
  const authCode2: string = tokenObject?.auth_code2 ?? '';
  const authCode: string = tokenObject?.auth_code ?? 'None';

  // Share-link permission flags (allowReports, allowDownloading, etc.)
  const tokenProperties = !!(tokenObject && 'allowDownloading' in tokenObject);

  // --- StudyInstanceUIDs from URL ---
  const urlStudyUIDs: string[] = new URLSearchParams(location.search)
    .getAll('StudyInstanceUIDs')
    .flatMap(s => s.split(','))
    .map(s => s.trim())
    .filter(Boolean);

  const isShareRoute = location.pathname === '/share';

  // --- Determine initial state synchronously ---
  // Mode 1: /share + auth_code2 non-empty  → compare URL UIDs with token.Studies (sync)
  // Mode 2: not /share + auth_code non-empty → filtered upstream by PanelStudyBrowser (no work here)
  // Mode 3: auth_code is empty             → load all studies, no filtering (sync)

  let initialAuthorizedUIDs: string[] | null = null;
  let initialError: string | null = null;

  if (isShareRoute && authCode2) {
    // Mode 1 — validate synchronously
    const isValid =
      urlStudyUIDs.length > 0 && urlStudyUIDs.every(uid => tokenStudies.includes(uid));
    initialAuthorizedUIDs = isValid ? urlStudyUIDs : [];
    initialError = isValid ? null : 'Unauthorized: study access denied';
  }
  // Mode 2: filtering handled upstream in PanelStudyBrowser via GetStudiesByAuthCode cache
  // Mode 3: initialAuthorizedUIDs stays null → show all

  const [authorizedStudyUIDs] = useState<string[] | null>(initialAuthorizedUIDs);
  const [error] = useState<string | null>(initialError);

  const getTabContent = () => {
    if (error) {
      return <div className="text-red-500 p-4">{error}</div>;
    }

    const tabData = tabs.find(tab =>
      tokenProperties ? tab.name === 'primary' : tab.name === activeTabName
    );

    if (!tabData) return null;

    // authorizedStudyUIDs === null  → Mode 3 or Mode 2 (pre-filtered upstream): show all
    // authorizedStudyUIDs === []    → access denied (already caught above by error state)
    // authorizedStudyUIDs = string[] → Mode 1: filter to authorized UIDs
    let displayStudies =
      authorizedStudyUIDs !== null
        ? tabData.studies.filter(study =>
            authorizedStudyUIDs.includes(study.studyInstanceUid)
          )
        : tabData.studies;

    // Filter DOC modality on share links when reports are not permitted
    if (tokenProperties && tokenObject?.allowReports === 'False') {
      displayStudies = displayStudies.map(study => {
        if (!study.modalities.includes('DOC')) return study;
        return {
          ...study,
          displaySets: study.displaySets.filter(ds => ds.modality !== 'DOC'),
        };
      });
    }

    return displayStudies.map(
      ({ studyInstanceUid, date, description, numInstances, modalities, displaySets }) => {
        const isExpanded = expandedStudyInstanceUIDs.includes(studyInstanceUid);
        return (
          <React.Fragment key={studyInstanceUid}>
            <StudyItem
              date={date}
              description={description}
              numInstances={numInstances}
              modalities={modalities}
              trackedSeries={getTrackedSeries(displaySets)}
              isActive={isExpanded}
              onClick={() => {
                onClickStudy(studyInstanceUid);
              }}
              data-cy="thumbnail-list"
            />
            {isExpanded && displaySets && displaySets.length > 0 && (
              <ThumbnailList
                thumbnails={displaySets}
                activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
                onThumbnailClick={onClickThumbnail}
                onThumbnailDoubleClick={onDoubleClickThumbnail}
                onClickUntrack={onClickUntrack}
              />
            )}
          </React.Fragment>
        );
      }
    );
  };

  return (
    <React.Fragment>
      <div
        className="w-100 border-secondary-light bg-primary-dark flex h-16 flex-row items-center justify-center border-b p-4"
        data-cy={'studyBrowser-panel'}
      >
        {/* TODO Revisit design of LegacyButtonGroup later - for now use LegacyButton for its children.*/}
        <LegacyButtonGroup
          variant="outlined"
          color="secondary"
          splitBorder={false}
        >
          {tabs.map(tab => {
            const { name, label, studies } = tab;
            const isActive = activeTabName === name;
            const isDisabled = !studies.length;
            const classStudyBrowser = customizationService?.getModeCustomization(
              'class:StudyBrowser'
            ) || {
              true: 'default',
              false: 'default',
            };
            const color = classStudyBrowser[`${isActive}`];
            return (
              <LegacyButton
                key={name}
                className={'min-w-18 p-2 text-base text-white'}
                size="initial"
                color={color}
                bgColor={isActive ? 'bg-primary-main' : 'bg-black'}
                onClick={() => {
                  onClickTab(name);
                }}
                disabled={isDisabled}
              >
                {t(label)}
              </LegacyButton>
            );
          })}
        </LegacyButtonGroup>
      </div>
      <div className="ohif-scrollbar invisible-scrollbar flex flex-1 flex-col overflow-auto">
        {getTabContent()}
      </div>
    </React.Fragment>
  );
};

StudyBrowser.propTypes = {
  onClickTab: PropTypes.func.isRequired,
  onClickStudy: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  activeTabName: PropTypes.string.isRequired,
  expandedStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      studies: PropTypes.arrayOf(
        PropTypes.shape({
          studyInstanceUid: PropTypes.string.isRequired,
          date: PropTypes.string,
          numInstances: PropTypes.number,
          modalities: PropTypes.string,
          description: PropTypes.string,
          displaySets: PropTypes.arrayOf(
            PropTypes.shape({
              displaySetInstanceUID: PropTypes.string.isRequired,
              imageSrc: PropTypes.string,
              imageAltText: PropTypes.string,
              seriesDate: PropTypes.string,
              seriesNumber: StringNumber,
              numInstances: PropTypes.number,
              description: PropTypes.string,
              componentType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage'])
                .isRequired,
              isTracked: PropTypes.bool,
              /**
               * Data the thumbnail should expose to a receiving drop target. Use a matching
               * `dragData.type` to identify which targets can receive this draggable item.
               * If this is not set, drag-n-drop will be disabled for this thumbnail.
               *
               * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
               */
              dragData: PropTypes.shape({
                /** Must match the "type" a dropTarget expects */
                type: PropTypes.string.isRequired,
              }),
            })
          ),
        })
      ).isRequired,
    })
  ),
};

const noop = () => {};

StudyBrowser.defaultProps = {
  onClickTab: noop,
  onClickStudy: noop,
  onClickThumbnail: noop,
  onDoubleClickThumbnail: noop,
  onClickUntrack: noop,
};

export default StudyBrowser;
